import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, Grid, List, Clock, AlertCircle, Loader, Tag, BarChart2, Table, RefreshCw, Trash2, Sparkles, Coins, CheckCircle, Plus, X, Filter, ArrowUpDown, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import {
  NoDocumentsEmptyState, NoEntitiesEmptyState, NoSearchResultsEmptyState, NoFilterResultsEmptyState,
} from './EmptyStates';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { formatBytes, formatDate } from '../utils/formatters';
import { config } from '../utils/config';
import { apiClient } from '../api/client';
import PDFViewer from './PDFViewer';
import EntityCorrectionWidget from './EntityCorrectionWidget';
import AddEntityWidget from './AddEntityWidget';
import TableViewer from './TableViewer';
import ExtractionMethodBadge from './ExtractionMethodBadge';
import TemplateMatchModal from './TemplateMatchModal';
import InvoicesSubNav from './InvoicesSubNav';
import { friendlyFieldName } from '../utils/entityLabels';
import { useDismissOnEscape } from '../hooks/useDismissOnEscape';
import { useToast } from './ui/Toast';
import { useConfirm } from './ui/ConfirmDialog';
import { errorMessage } from '../utils/errors';
import '../styles/invoices.css';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchDocuments, removeDocument, clearError, updateDocumentStatus } from '../store/documentSlice';
import { AnalysisResults, Entity, TableData, InvoiceStatus, InvoiceDirection } from '../types';
import { getInvoiceStatusLabel } from '../api/invoices';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface AnalysisResponse {
  entities: Record<string, Entity[]>;
  total_count: number;
  tables?: TableData[];
}

const UploadedInvoicesPage = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { documents, loading, error } = useAppSelector(state => state.documents);

  // Get documentId from URL params for auto-selection
  const documentIdFromUrl = searchParams.get('documentId');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<AnalysisResults | null>(null);
  const [analysisDetails, setAnalysisDetails] = useState<AnalysisResponse | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'document' | 'entities' | 'tables'>('overview');
  const [deleting, setDeleting] = useState<number | null>(null);

  // Filter and Sort state
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'processing' | 'failed'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);

  const [statusUpdating, setStatusUpdating] = useState(false);

  // Template match modal state
  const [showTemplateMatchModal, setShowTemplateMatchModal] = useState(false);
  const [templateMatchInfo, setTemplateMatchInfo] = useState<{
    template_id: number;
    template_name: string;
    confidence: number;
    field_count: number;
  } | null>(null);

  useEffect(() => {
    // Always fetch fresh documents when the page mounts to ensure data is up-to-date
    dispatch(fetchDocuments());
  }, [dispatch]);

  // Auto-select document from URL param
  useEffect(() => {
    if (documentIdFromUrl && documents.length > 0) {
      const docId = parseInt(documentIdFromUrl, 10);
      const doc = documents.find(d => d.id === docId);
      if (doc) {
        setSelectedDocument(doc);
        // Scroll to the document card
        setTimeout(() => {
          const element = document.querySelector(`[data-document-id="${docId}"]`);
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [documentIdFromUrl, documents]);

  // One close path for the slide-over, shared by the X, Escape and the backdrop.
  const closeDocumentDetails = useCallback(() => {
    setSelectedDocument(null);
    setAnalysisDetails(null);
    setActiveTab('overview');
    setAnalysisError(null);
  }, []);

  useDismissOnEscape(!!selectedDocument, closeDocumentDetails);

  useEffect(() => {
    const controller = new AbortController();

    if (selectedDocument) {
      fetchAnalysisDetails(selectedDocument.id, controller.signal);
    } else {
      setAnalysisDetails(null);
      setAnalysisError(null);
    }

    return () => {
      controller.abort();
    };
  }, [selectedDocument]);

  // Check for template match and show confirmation modal
  useEffect(() => {
    if (selectedDocument?.last_analysis?.analysis_metadata?.template_match) {
      const matchData = selectedDocument.last_analysis.analysis_metadata.template_match;

      // Only show modal if requires confirmation and not already matched
      if (matchData.requires_confirmation && !selectedDocument.last_analysis.analysis_metadata.template_matched) {
        setTemplateMatchInfo({
          template_id: matchData.template_id,
          template_name: matchData.template_name,
          confidence: matchData.confidence,
          field_count: matchData.field_count
        });
        setShowTemplateMatchModal(true);
      }
    }
  }, [selectedDocument]);

  const fetchAnalysisDetails = async (documentId: number, signal?: AbortSignal) => {
    try {
      setLoadingAnalysis(true);
      setAnalysisError(null);
      setAnalysisDetails(null);

      const response = await apiClient.get(
        `${config.endpoints.documents}/${documentId}/entities`,
        { signal }
      );
      setAnalysisDetails(response.data);
    } catch (err) {
      // Don't show error if request was aborted (component unmounted or document changed)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      if (err instanceof Error) {
        setAnalysisError(err.message);
      } else {
        setAnalysisError('Failed to load entity data');
      }
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleDeleteDocument = async (documentId: number, e: React.MouseEvent) => {
    e.stopPropagation();

    const doc = documents.find((d) => d.id === documentId);
    const ok = await confirm({
      title: 'Delete this document?',
      message: (
        <>
          <b>{doc?.metadata?.filename ?? 'This document'}</b> and everything extracted from it
          will be permanently removed. This cannot be undone.
        </>
      ),
      confirmLabel: 'Delete document',
      destructive: true,
    });
    if (!ok) return;

    try {
      setDeleting(documentId);
      await apiClient.delete(`${config.endpoints.documents}/${documentId}`);

      // Remove from Redux state
      dispatch(removeDocument(documentId));

      // Close detail view if deleting selected document
      if (selectedDocument?.id === documentId) {
        closeDocumentDetails();
      }
      toast.success('Document deleted.');
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to delete the document.'));
    } finally {
      setDeleting(null);
    }
  };

  const handleUpdateStatus = async (status: InvoiceStatus, direction?: InvoiceDirection) => {
    if (!selectedDocument) return;
    try {
      setStatusUpdating(true);
      await dispatch(updateDocumentStatus({
        id: selectedDocument.id,
        invoice_status: status,
        invoice_direction: direction ?? selectedDocument.invoice_direction ?? 'outgoing',
      })).unwrap();
      // Update local selected document to reflect change immediately
      setSelectedDocument(prev => prev ? {
        ...prev,
        invoice_status: status,
        invoice_direction: direction ?? prev.invoice_direction,
      } : prev);
    } catch (err) {
      console.error('Failed to update document status:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleUpdateDirection = async (direction: InvoiceDirection) => {
    if (!selectedDocument) return;
    try {
      setStatusUpdating(true);
      await dispatch(updateDocumentStatus({
        id: selectedDocument.id,
        invoice_direction: direction,
      })).unwrap();
      setSelectedDocument(prev => prev ? { ...prev, invoice_direction: direction } : prev);
    } catch (err) {
      console.error('Failed to update direction:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleRefreshAnalysis = () => {
    if (selectedDocument) {
      fetchAnalysisDetails(selectedDocument.id);
    }
  };

  // Template match handlers
  const handleAcceptTemplate = async () => {
    if (!selectedDocument || !templateMatchInfo) return;

    try {
      // Apply template validation to document
      await apiClient.post(`${config.endpoints.documents}/${selectedDocument.id}/apply-template`, {
        template_id: templateMatchInfo.template_id
      });

      // Refresh document to get updated analysis with template
      await handleRefreshAnalysis();

      setShowTemplateMatchModal(false);
      setTemplateMatchInfo(null);
    } catch (error) {
      console.error('Failed to apply template:', error);
      setAnalysisError('Failed to apply template');
    }
  };

  const handleDeclineTemplate = () => {
    // User declined template - continue with hybrid-only
    setShowTemplateMatchModal(false);
    setTemplateMatchInfo(null);
  };

  // Filter documents by search, status, and sort
  const filteredDocuments = documents
    .filter(doc => {
      // Search filter
      const matchesSearch = doc.metadata.filename.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const status = doc.last_analysis?.status || 'processing';
      const matchesStatus = filterStatus === 'all' || status === filterStatus;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          const dateA = new Date(a.upload_date || a.metadata.analysis_timestamp).getTime();
          const dateB = new Date(b.upload_date || b.metadata.analysis_timestamp).getTime();
          comparison = dateA - dateB;
          break;
        case 'name':
          comparison = a.metadata.filename.localeCompare(b.metadata.filename);
          break;
        case 'size':
          comparison = a.metadata.file_size - b.metadata.file_size;
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // The "AI Assist" chips that used to live here were driven by
  // analysis_metadata.validation_score and .incomplete_fields_count. Neither
  // field is produced anywhere in the backend, so getAIChipInfo always returned
  // null and the chips never rendered. Removed along with their click handler.

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-500/20 text-green-300';
      case 'confirmed': return 'bg-coral-500/20 text-foreground-muted';
      case 'overdue': return 'bg-red-500/20 text-red-300';
      default: return 'bg-blue-500/20 text-blue-300'; // ready_to_process
    }
  };

  const renderDocumentCard = (document: AnalysisResults) => {

    const isHighlighted = documentIdFromUrl && parseInt(documentIdFromUrl, 10) === document.id;

    return (
    <Card
      key={document.id}
      data-document-id={document.id}
      className={`hover:bg-coral-500/10 transition-all duration-200 cursor-pointer relative group ${isHighlighted ? 'ring-2 ring-coral-500' : ''}`}
      onClick={() => setSelectedDocument(document)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2 rounded-lg bg-coral-500/10">
              <FileText className="w-6 h-6 text-coral-400" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base text-foreground truncate">
                {document.metadata.filename}
              </CardTitle>
              <p className="text-sm text-foreground-muted">
                {formatBytes(document.metadata.file_size)} • {document.metadata.total_pages} pages
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2 flex-wrap justify-end">
            {/* Labelled by the money flow, not the document direction: an "IN"
                badge on a bill you owe read as money coming in. */}
            {document.invoice_direction === 'incoming' && (
              <div
                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap"
                title="Incoming invoice — a bill you pay, so it counts as money out"
              >
                MONEY OUT
              </div>
            )}
            {document.invoice_status && (
              <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${getInvoiceStatusColor(document.invoice_status)}`}>
                {getInvoiceStatusLabel(document.invoice_status as InvoiceStatus, document.invoice_direction as InvoiceDirection | undefined)}
              </div>
            )}
            <div className={`px-2 py-1 rounded-full text-xs
              ${document.last_analysis?.status === 'completed'
                ? 'bg-green-500/20 text-green-300'
                : document.last_analysis?.status === 'failed'
                ? 'bg-red-500/20 text-red-300'
                : 'bg-coral-500/20 text-foreground-muted'
              }`}>
              {document.last_analysis?.status || 'Processing'}
            </div>
            <button
              onClick={(e) => handleDeleteDocument(document.id, e)}
              disabled={deleting === document.id}
              className={`p-1 text-coral-400 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors opacity-0 group-hover:opacity-100 ${
                deleting === document.id ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Delete document"
            >
              {deleting === document.id ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-foreground-muted">
            <Clock className="w-4 h-4" />
            {formatDate(document.upload_date || document.metadata.analysis_timestamp)}
          </div>
        </div>

      </CardContent>
    </Card>
    );
  };

  const renderDocumentRow = (document: AnalysisResults) => {

    return (
    <div
      key={document.id}
      className="flex items-center gap-4 p-4 hover:bg-coral-500/10 transition-all duration-200 cursor-pointer rounded-lg group"
      onClick={() => setSelectedDocument(document)}
    >
      <div className="p-2 rounded-lg bg-coral-500/10">
        <FileText className="w-6 h-6 text-coral-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base text-foreground truncate">{document.metadata.filename}</h3>
        <p className="text-sm text-foreground-secondary">
          {formatBytes(document.metadata.file_size)} • {document.metadata.total_pages} pages
        </p>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-sm text-foreground-secondary">
          {formatDate(document.upload_date || document.metadata.analysis_timestamp)}
        </div>
        {document.invoice_direction === 'incoming' && (
          <div
            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap"
            title="Incoming invoice — a bill you pay, so it counts as money out"
          >
            MONEY OUT
          </div>
        )}
        {document.invoice_status && (
          <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${getInvoiceStatusColor(document.invoice_status)}`}>
            {getInvoiceStatusLabel(document.invoice_status as InvoiceStatus, document.invoice_direction as InvoiceDirection | undefined)}
          </div>
        )}
        <div className={`px-2 py-1 rounded-full text-xs
          ${document.last_analysis?.status === 'completed'
            ? 'bg-green-500/20 text-green-300'
            : document.last_analysis?.status === 'failed'
            ? 'bg-red-500/20 text-red-300'
            : 'bg-coral-500/20 text-foreground-muted'
          }`}>
          {document.last_analysis?.status || 'Processing'}
        </div>


        <button
          onClick={(e) => handleDeleteDocument(document.id, e)}
          disabled={deleting === document.id}
          className={`p-1 text-coral-400 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors opacity-0 group-hover:opacity-100 ${
            deleting === document.id ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          title="Delete document"
        >
          {deleting === document.id ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
    );
  };

  const renderEntityCard = (entity: Entity, index: number) => {
    if (!entity || !entity.id) {
      console.error('Invalid entity:', entity);
      return null;
    }

    return (
      <EntityCorrectionWidget
        key={`${entity.id}-${entity.start_char}-${index}`}
        entity={entity}
        onCorrectionSubmitted={() => {
          // Refresh the analysis after correction
          if (selectedDocument) {
            fetchAnalysisDetails(selectedDocument.id);
          }
        }}
        showConfidence={true}
        documentId={selectedDocument?.id}
      />
    );
  };

  // Group entities by their label
  const groupedEntities = analysisDetails?.entities || {};

  return (
    <div className="inv-container">
      <InvoicesSubNav />
      {/* Header */}
      <div className="inv-header" style={{ marginTop: 24 }}>
        <div>
          <div className="inv-eyebrow"><span className="dot" />Money in · invoices you receive</div>
          <h1>Uploaded Invoices</h1>
          <p className="sub">Drop in PDFs and review what ViewPoint read from them.</p>
        </div>
        <div className="inv-header-actions">
          <button
            onClick={() => navigate('/dashboard')}
            className="inv-btn primary"
            aria-label="Upload new document"
          >
            <Plus />
            <span className="hidden sm:inline">Upload New</span>
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`inv-btn icon-btn${viewMode === 'grid' ? ' active' : ''}`}
            aria-label="Grid view"
          >
            <Grid />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`inv-btn icon-btn${viewMode === 'list' ? ' active' : ''}`}
            aria-label="List view"
          >
            <List />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-3">
        <div className="inv-toolbar" style={{ marginBottom: 0 }}>
          <div className="inv-search">
            <Search />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inv-filter-toggle${showFilters || filterStatus !== 'all' ? ' active' : ''}`}
          >
            <Filter />
            <span className="hidden sm:inline">Filter</span>
            {filterStatus !== 'all' && (
              <span className="w-2 h-2 rounded-full bg-coral-400" />
            )}
          </button>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="inv-filter-toggle"
          >
            <ArrowUpDown className={`transition-transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
            <span className="hidden sm:inline">Sort</span>
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="inv-panel" style={{ marginBottom: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600 dark:text-foreground-muted">Status:</span>
              <div className="flex gap-1">
                {(['all', 'completed', 'processing', 'failed'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      filterStatus === status
                        ? 'bg-coral-500 text-white'
                        : 'bg-stone-200 dark:bg-coral-500/20 text-stone-700 dark:text-foreground-muted hover:bg-stone-300 dark:hover:bg-coral-500/30'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-6 w-px bg-stone-300 dark:bg-coral-500/30 hidden sm:block" />
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600 dark:text-foreground-muted">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'size')}
                className="px-3 py-1 text-sm bg-stone-200 dark:bg-coral-500/20 border-none rounded-lg text-stone-700 dark:text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-coral-500/40"
              >
                <option value="date">Date</option>
                <option value="name">Name</option>
                <option value="size">Size</option>
              </select>
            </div>
            {(filterStatus !== 'all' || sortBy !== 'date') && (
              <button
                onClick={() => {
                  setFilterStatus('all');
                  setSortBy('date');
                  setSortOrder('desc');
                }}
                className="ml-auto text-xs text-coral-400 hover:text-foreground underline"
              >
                Reset filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <Loader className="w-8 h-8 text-coral-400 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-200 mb-6">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
          <button
            onClick={() => dispatch(clearError())}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Analysis Error State */}
      {analysisError && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-200 mb-6">
          <AlertCircle className="w-5 h-5" />
          <p>{analysisError}</p>
          <button
            onClick={() => setAnalysisError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Empty State — distinguish "you have nothing" from "your filter matched
          nothing". The latter used to claim "No documents yet" and offer a CTA
          that navigated away instead of clearing the filter. */}
      {!loading && !error && filteredDocuments.length === 0 && (
        documents.length === 0 ? (
          <NoDocumentsEmptyState onUpload={() => navigate('/dashboard')} />
        ) : searchTerm ? (
          <NoSearchResultsEmptyState searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
        ) : (
          <NoFilterResultsEmptyState onClearFilters={() => setFilterStatus('all')} />
        )
      )}

      {/* Document Grid/List */}
      {!loading && !error && filteredDocuments.length > 0 && (
        <div className={
          viewMode === 'grid'
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            : "space-y-2"
        }>
          {filteredDocuments.map(doc =>
            viewMode === 'grid' ? renderDocumentCard(doc) : renderDocumentRow(doc)
          )}
        </div>
      )}

      {/* Document Details Slide-Over */}
      {selectedDocument && (
        <div
          className="fixed inset-0 bg-black/50 z-50"
          // Clicking the dimmed area closes the slide-over, matching every other
          // overlay in the app. Guarded on target so a click inside the panel
          // does not bubble up and close it.
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeDocumentDetails(); }}
        >
          <div className="absolute inset-y-0 right-0 w-full sm:w-[90vw] md:w-[85vw] lg:w-3/4 xl:w-3/4 bg-stone-50 dark:bg-background-secondary shadow-xl border-l border-stone-200 dark:border-coral-500/30">
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-coral-500/20">
                <h2 className="text-lg font-medium text-stone-900 dark:text-foreground">
                  Document Details
                </h2>
                <button
                  onClick={closeDocumentDetails}
                  className="text-coral-400 hover:text-foreground hover:bg-coral-500/10 rounded-lg p-2 transition-all"
                  aria-label="Close document details"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* macOS-Style Segmented Control Tabs */}
              <div className="flex justify-center p-4 bg-coral-500/5">
                <div className="inline-flex p-1 bg-coral-500/10 rounded-xl gap-1">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === 'overview'
                        ? 'bg-coral-500/30 text-foreground shadow-sm'
                        : 'text-foreground-muted hover:text-foreground-secondary hover:bg-coral-500/10'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <BarChart2 className="w-4 h-4" />
                      <span>Overview</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('document')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === 'document'
                        ? 'bg-coral-500/30 text-foreground shadow-sm'
                        : 'text-foreground-muted hover:text-foreground-secondary hover:bg-coral-500/10'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      <span>Document</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('entities')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === 'entities'
                        ? 'bg-coral-500/30 text-foreground shadow-sm'
                        : 'text-foreground-muted hover:text-foreground-secondary hover:bg-coral-500/10'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      <span>Details</span>
                      {analysisDetails && analysisDetails.total_count > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-coral-500/30 text-foreground-secondary">
                          {analysisDetails.total_count}
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('tables')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === 'tables'
                        ? 'bg-coral-500/30 text-foreground shadow-sm'
                        : 'text-foreground-muted hover:text-foreground-secondary hover:bg-coral-500/10'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Table className="w-4 h-4" />
                      <span>Items</span>
                      {analysisDetails?.tables && analysisDetails.tables.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-green-500/30 text-green-200">
                          {analysisDetails.tables.length}
                        </span>
                      )}
                    </div>
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {loadingAnalysis && activeTab !== 'document' ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center">
                      <Loader className="w-8 h-8 text-coral-400 animate-spin mb-4" />
                      <p className="text-foreground-muted">Loading analysis details...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {activeTab === 'overview' && (
                      <div className="p-6 space-y-4">
                        {/* Compact Invoice Summary */}
                        <div className="glass-effect rounded-2xl p-5 border border-coral-500/20 bg-gradient-to-br from-coral-500/5 to-blue-500/5">
                          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-coral-400" />
                            Invoice Summary
                          </h3>

                          {(() => {
                            const invoiceNumber = analysisDetails?.entities['INVOICE_NUMBER']?.[0]?.text;
                            const invoiceDate = analysisDetails?.entities['INVOICE_DATE']?.[0]?.text;
                            const dueDate = analysisDetails?.entities['DUE_DATE']?.[0]?.text;
                            const vendor = analysisDetails?.entities['SELLER_NAME']?.[0]?.text ||
                                          analysisDetails?.entities['SELLER']?.[0]?.text;
                            const totalAmount = analysisDetails?.entities['INVOICE_AMOUNT']?.[0]?.text ||
                                               analysisDetails?.entities['TOTAL_AMOUNT']?.[0]?.text;
                            const currency = analysisDetails?.entities['CURRENCY']?.[0]?.text || '';

                            return (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="p-3 rounded-xl bg-coral-500/10">
                                    <p className="text-xs text-foreground-muted mb-1">Invoice Number</p>
                                    <p className="text-lg font-semibold text-white truncate">
                                      {invoiceNumber || 'Not detected'}
                                    </p>
                                  </div>
                                  <div className="p-3 rounded-xl bg-coral-500/10">
                                    <p className="text-xs text-foreground-muted mb-1">Invoice Date</p>
                                    <p className="text-lg font-semibold text-white">
                                      {invoiceDate || 'Not detected'}
                                    </p>
                                  </div>
                                  <div className="p-3 rounded-xl bg-coral-500/10">
                                    <p className="text-xs text-foreground-muted mb-1">Vendor</p>
                                    <p className="text-lg font-semibold text-white truncate">
                                      {vendor || 'Not detected'}
                                    </p>
                                  </div>
                                  <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                                    <p className="text-xs text-green-300/70 mb-1">Total Amount</p>
                                    <p className="text-xl font-bold text-green-200">
                                      {totalAmount ? `${currency} ${totalAmount}` : 'Not detected'}
                                    </p>
                                  </div>
                                </div>
                                {dueDate && (
                                  <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                                    <p className="text-xs text-yellow-300/70 mb-1">Due Date</p>
                                    <p className="text-sm font-medium text-yellow-200">{dueDate}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Compact Confidence & Stats */}
                        {selectedDocument?.last_analysis?.analysis_metadata && (
                          <div className="glass-effect rounded-2xl p-4 border border-coral-500/20">
                            {(() => {
                              const metadata = selectedDocument.last_analysis.analysis_metadata;
                              const avgConfidence = metadata.avg_confidence || 0;
                              const confidencePercentage = Math.round(avgConfidence * 100);
                              const totalEntities = analysisDetails?.total_count || 0;

                              return (
                                <div className="flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                      confidencePercentage >= 80 ? 'bg-green-500/20 text-green-400' :
                                      confidencePercentage >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                                      'bg-red-500/20 text-red-400'
                                    }`}>
                                      <span className="text-sm font-bold">{confidencePercentage}%</span>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-white">How sure we are</p>
                                      <p className="text-xs text-foreground-muted">{totalEntities} details read from this invoice</p>
                                    </div>
                                  </div>
                                  <ExtractionMethodBadge
                                    extractionMethod={metadata.extraction_method}
                                    usedClaude={metadata.used_claude}
                                    learningImprovements={metadata.learning_improvements || 0}
                                    templateName={metadata.template_name}
                                    size="sm"
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        )}

                        {/* Invoice Status Management */}
                        <div className="glass-effect rounded-2xl p-5 border border-coral-500/20">
                          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-coral-400" />
                            Invoice Status
                          </h3>

                          {/* Direction picker */}
                          <div className="mb-4">
                            <p className="text-xs text-foreground-muted mb-2">Type</p>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() => handleUpdateDirection('outgoing')}
                                disabled={statusUpdating}
                                className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-xs ${
                                  selectedDocument.invoice_direction === 'outgoing' || !selectedDocument.invoice_direction
                                    ? 'bg-coral-500/20 text-foreground-muted border-coral-500/50 ring-1 ring-coral-500/30'
                                    : 'bg-coral-500/5 border-coral-500/20 text-coral-400 hover:bg-coral-500/10'
                                }`}
                              >
                                <ArrowUpRight className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                                <span className="font-medium">Outgoing</span>
                                <span className="text-foreground-muted hidden lg:inline">· money in</span>
                              </button>
                              <button
                                onClick={() => handleUpdateDirection('incoming')}
                                disabled={statusUpdating}
                                className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-xs ${
                                  selectedDocument.invoice_direction === 'incoming'
                                    ? 'bg-blue-500/20 text-blue-300 border-blue-500/50 ring-1 ring-blue-500/30'
                                    : 'bg-coral-500/5 border-coral-500/20 text-coral-400 hover:bg-coral-500/10'
                                }`}
                              >
                                <ArrowDownLeft className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                <span className="font-medium">Incoming</span>
                                <span className="text-foreground-muted hidden lg:inline">· money out</span>
                              </button>
                            </div>
                          </div>

                          {/* Status buttons */}
                          <div>
                            <p className="text-xs text-foreground-muted mb-2">Status</p>
                            <div className="flex flex-wrap gap-2">
                              {(selectedDocument.invoice_direction === 'incoming'
                                ? [
                                    { value: 'ready_to_process' as InvoiceStatus, label: 'Received' },
                                    { value: 'confirmed' as InvoiceStatus, label: 'Approved to Pay' },
                                    { value: 'paid' as InvoiceStatus, label: 'Paid' },
                                  ]
                                : [
                                    { value: 'ready_to_process' as InvoiceStatus, label: 'Ready to Process' },
                                    { value: 'confirmed' as InvoiceStatus, label: 'Confirmed' },
                                    { value: 'paid' as InvoiceStatus, label: 'Paid' },
                                  ]
                              ).map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => handleUpdateStatus(opt.value)}
                                  disabled={statusUpdating}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                    selectedDocument.invoice_status === opt.value
                                      ? getInvoiceStatusColor(opt.value) + ' ring-1 ring-current border-current'
                                      : 'bg-coral-500/5 border-coral-500/20 text-coral-400 hover:bg-coral-500/10'
                                  } ${statusUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {statusUpdating && selectedDocument.invoice_status !== opt.value ? opt.label : opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="flex gap-3">
                          <button
                            onClick={() => navigate('/ai', {
                              state: {
                                prefillMessage: `Summarise this invoice (${selectedDocument.metadata.filename}): who it's from, the total, the due date, and anything that needs my attention.`,
                                contextData: {
                                  documentId: selectedDocument.id,
                                  filename: selectedDocument.metadata.filename,
                                  entities: analysisDetails?.entities,
                                },
                                source: 'uploaded_invoice_detail',
                                // Pin it, so follow-up questions stay about this invoice.
                                invoiceContext: {
                                  invoiceNumber: analysisDetails?.entities?.['INVOICE_NUMBER']?.[0]?.text
                                    || selectedDocument.metadata.filename,
                                  clientName: selectedDocument.invoice_direction === 'incoming'
                                    ? analysisDetails?.entities?.['SELLER_NAME']?.[0]?.text
                                    : analysisDetails?.entities?.['BUYER_NAME']?.[0]?.text,
                                  documentId: selectedDocument.id,
                                },
                              },
                            })}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/30 text-purple-700 dark:text-purple-200 transition-all"
                          >
                            <Sparkles className="w-4 h-4" />
                            <span className="text-sm font-medium">Ask AI</span>
                          </button>
                          <button
                            onClick={handleRefreshAnalysis}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-coral-500/20 hover:bg-coral-500/30 border border-coral-500/30 text-foreground-secondary transition-all"
                          >
                            <RefreshCw className="w-4 h-4" />
                            <span className="text-sm font-medium">Read again</span>
                          </button>
                          <button
                            onClick={() => setActiveTab('entities')}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-200 transition-all"
                          >
                            <Tag className="w-4 h-4" />
                            <span className="text-sm font-medium">View all details</span>
                          </button>
                          <button
                            onClick={(e) => handleDeleteDocument(selectedDocument.id, e)}
                            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {activeTab === 'document' && (
                      <div className="h-full">
                        <PDFViewer
                          documentId={selectedDocument.id}
                          filename={selectedDocument.metadata.filename}
                        />
                      </div>
                    )}

                    {activeTab === 'entities' && (
                      <div className="flex h-full">
                        {/* Left Side: PDF Viewer (50%) */}
                        <div className="w-1/2 border-r border-coral-500/20">
                          <PDFViewer
                            documentId={selectedDocument.id}
                            filename={selectedDocument.metadata.filename}
                          />
                        </div>

                        {/* Right Side: Entities (50%) */}
                        <div className="w-1/2 overflow-y-auto">
                          <div className="p-6 flex gap-4">
                            {/* Main content area */}
                            <div className="flex-1 space-y-6">
                              <div>
                                <h3 className="text-base font-semibold text-foreground">
                                  Check &amp; fix details
                                </h3>
                                <p className="text-sm text-foreground-muted mt-1">
                                  Compare each value with the invoice on the left. Click a value to fix it.
                                </p>
                              </div>

                              {/* Template Match Indicator */}
                              {selectedDocument?.last_analysis?.analysis_metadata?.template_matched && (
                                <div className="glass-effect rounded-2xl p-4 border border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/10">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-green-500/20">
                                      <FileText className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="text-sm font-semibold text-green-100 mb-1 flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4" />
                                        Read from your saved template
                                      </h3>
                                      <p className="text-xs text-green-200/80">
                                        We recognised this supplier from your <span className="font-semibold">{selectedDocument.last_analysis.analysis_metadata.template_name}</span> template, so the details were filled in automatically.
                                      </p>
                                      <div className="mt-2 flex items-center gap-4 text-xs">
                                        <span className="text-green-300">
                                          {selectedDocument.last_analysis.analysis_metadata.template_fields_extracted || 0} details filled in
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Entities List with Real-Time Feedback */}
                              {analysisError ? (
                                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                                  <div className="flex items-center gap-2 text-red-200">
                                    <AlertCircle className="w-5 h-5" />
                                    <p>{analysisError}</p>
                                  </div>
                                  <div className="mt-4 flex justify-center">
                                    <button
                                      onClick={handleRefreshAnalysis}
                                      className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-300 transition-all"
                                    >
                                      <RefreshCw className="w-4 h-4" />
                                      Try Again
                                    </button>
                                  </div>
                                </div>
                              ) : analysisDetails && Object.keys(groupedEntities).length > 0 ? (
                                <div className="space-y-6">
                                  {Object.entries(groupedEntities).map(([label, entities]) => (
                                    <div key={label} className="glass-effect rounded-xl p-4 border border-coral-500/10">
                                      <h4 className="text-sm font-medium text-foreground-secondary mb-3 flex items-center gap-2">
                                        <Tag className="w-3.5 h-3.5 text-coral-400" />
                                        {friendlyFieldName(label)}
                                        <span className="text-xs text-foreground-muted">({entities.length})</span>
                                      </h4>
                                      <div className="space-y-3">
                                        {entities.map((entity, index) => renderEntityCard(entity, index))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <NoEntitiesEmptyState onRefresh={handleRefreshAnalysis} />
                              )}

                              {/* Add a field we missed */}
                              <AddEntityWidget
                                documentId={selectedDocument.id}
                                onEntityAdded={handleRefreshAnalysis}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'tables' && (
                      analysisDetails?.tables && analysisDetails.tables.length > 0 ? (
                        <TableViewer
                          tables={analysisDetails.tables}
                          documentName={selectedDocument.metadata.filename}
                        />
                      ) : (
                        <div className="p-6 space-y-6">
                          {/* No Tables - Show Line Items Breakdown */}
                          <div className="glass-effect rounded-2xl p-5 border border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
                            <h3 className="text-base font-semibold text-blue-100 mb-4 flex items-center gap-2">
                              <FileText className="w-5 h-5 text-blue-400" />
                              Line Items & Cost Breakdown
                            </h3>

                            {(() => {
                              const invoiceAmount = analysisDetails?.entities['INVOICE_AMOUNT']?.[0]?.text ||
                                                  analysisDetails?.entities['TOTAL_AMOUNT']?.[0]?.text;
                              const subtotal = analysisDetails?.entities['SUBTOTAL']?.[0]?.text;
                              const taxAmount = analysisDetails?.entities['TAX_AMOUNT']?.[0]?.text;
                              const hasBreakdown = subtotal || taxAmount;

                              return hasBreakdown ? (
                                <div className="space-y-4">
                                  <p className="text-sm text-blue-200/80 mb-4">
                                    This invoice doesn't contain a structured table, but we found these cost components:
                                  </p>

                                  <div className="bg-blue-500/10 rounded-xl p-4 space-y-3">
                                    {subtotal && (
                                      <div className="flex items-center justify-between text-sm">
                                        <span className="text-blue-200">Subtotal</span>
                                        <span className="text-blue-100 font-semibold font-mono">{subtotal}</span>
                                      </div>
                                    )}
                                    {taxAmount && (
                                      <div className="flex items-center justify-between text-sm">
                                        <span className="text-blue-200">Tax</span>
                                        <span className="text-blue-100 font-semibold font-mono">{taxAmount}</span>
                                      </div>
                                    )}
                                    {invoiceAmount && (
                                      <>
                                        <div className="border-t border-blue-500/30 my-2"></div>
                                        <div className="flex items-center justify-between">
                                          <span className="text-blue-100 font-semibold">Total</span>
                                          <span className="text-blue-100 font-bold text-lg font-mono">{invoiceAmount}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>

                                  <div className="mt-4 p-3 rounded-lg bg-coral-500/10 border border-coral-500/20">
                                    <p className="text-xs text-foreground-secondary">
                                      💡 <strong>Tip:</strong> All the values we read are in the "Details" tab, where you can review and fix them.
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  <p className="text-sm text-blue-200/80">
                                    This document doesn't contain structured tables or detailed line items.
                                  </p>

                                  <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 space-y-3">
                                    <h4 className="text-sm font-medium text-blue-100">This is common for:</h4>
                                    <ul className="space-y-2 text-sm text-blue-200/70">
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-400">•</span>
                                        <span>Simple service invoices (single line item)</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-400">•</span>
                                        <span>Summary invoices (totals only)</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-400">•</span>
                                        <span>Invoices with text-based descriptions instead of tables</span>
                                      </li>
                                    </ul>
                                  </div>

                                  {/* This used to open with "Good news: All invoice details were
                                      still extracted successfully!" — asserted unconditionally in
                                      the else-branch of a has-tables check, with nothing behind it.
                                      It fired identically whether 22 fields were read or 2. */}
                                  <div className="p-3 rounded-lg bg-coral-500/10 border border-coral-500/20">
                                    <p className="text-xs text-foreground-secondary">
                                      Check the "Overview" tab for the financial summary and the "Details" tab
                                      for everything we read, so you can confirm nothing is missing.
                                    </p>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Extracted Amounts Card */}
                          {analysisDetails && (
                            <div className="glass-effect rounded-2xl p-5 border border-coral-500/20">
                              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                                <Coins className="w-4 h-4 text-coral-400" />
                                Extracted Financial Data
                              </h3>

                              <div className="space-y-2">
                                {Object.entries(analysisDetails.entities)
                                  .filter(([label]) => ['INVOICE_AMOUNT', 'TOTAL_AMOUNT', 'SUBTOTAL', 'TAX_AMOUNT', 'MONEY'].includes(label))
                                  .map(([label, entities]) => (
                                    <div key={label} className="p-3 rounded-xl bg-coral-500/10 flex items-center justify-between">
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-foreground-secondary">{friendlyFieldName(label)}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                          <span className="text-xs text-foreground-muted">
                                            {Math.round((entities[0]?.confidence || 0) * 100)}% confidence
                                          </span>
                                          <div className="flex-1 h-1 bg-coral-500/20 rounded-full overflow-hidden max-w-[100px]">
                                            <div
                                              className={`h-full rounded-full ${
                                                (entities[0]?.confidence || 0) >= 0.8 ? 'bg-green-500' :
                                                (entities[0]?.confidence || 0) >= 0.6 ? 'bg-yellow-500' : 'bg-red-500'
                                              }`}
                                              style={{ width: `${(entities[0]?.confidence || 0) * 100}%` }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                      <span className="text-lg font-bold text-white font-mono ml-4">{entities[0]?.text}</span>
                                    </div>
                                  ))}

                                {Object.keys(analysisDetails.entities).filter(label =>
                                  ['INVOICE_AMOUNT', 'TOTAL_AMOUNT', 'SUBTOTAL', 'TAX_AMOUNT', 'MONEY'].includes(label)
                                ).length === 0 && (
                                  <div className="text-center py-6 text-foreground-muted text-sm">
                                    No financial amounts detected in this document
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Help Card */}
                          <div className="glass-effect rounded-2xl p-5 border border-yellow-500/20 bg-gradient-to-br from-yellow-500/5 to-orange-500/5">
                            <h3 className="text-sm font-semibold text-yellow-100 mb-3 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-yellow-400" />
                              Need Detailed Line Items?
                            </h3>

                            <div className="space-y-3 text-xs text-yellow-200/80">
                              <p>If you need itemized details, try these options:</p>
                              <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                  <span className="text-yellow-400">1.</span>
                                  <span><strong>Request from vendor:</strong> Ask for an invoice with a detailed table/itemization</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-yellow-400">2.</span>
                                  <span><strong>Check attachments:</strong> Some vendors send detailed breakdowns separately</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-yellow-400">3.</span>
                                  <span><strong>Manual entry:</strong> Use the "Details" tab to add fields we missed</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Match Confirmation Modal */}
      {showTemplateMatchModal && templateMatchInfo && (
        <TemplateMatchModal
          templateName={templateMatchInfo.template_name}
          confidence={templateMatchInfo.confidence}
          fieldCount={templateMatchInfo.field_count}
          onAccept={handleAcceptTemplate}
          onDecline={handleDeclineTemplate}
        />
      )}
    </div>
  );
};

// Keep backward compatibility with old name
export { UploadedInvoicesPage as DocumentsPage };
export default UploadedInvoicesPage;